import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { COMPANY_VIDEO, COMPANY } from "@/lib/assets";
import { Play, Pause } from "lucide-react";

export const VideoSection = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  return (
    <section ref={ref} data-testid="video-section" className="relative bg-foreground overflow-hidden">
      <div className="relative h-[70vh] min-h-[480px] overflow-hidden">
        <motion.video
          ref={videoRef}
          style={{ scale }}
          className="absolute inset-0 w-full h-full object-cover"
          src={COMPANY_VIDEO}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute inset-0 bg-foreground/45" />

        <div className="relative z-10 h-full flex flex-col justify-end">
          <div className="mx-auto max-w-[1400px] w-full px-5 md:px-10 pb-14">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-background/80 mb-5">/ On the ground</p>
              <h2 className="font-display text-background text-4xl md:text-7xl leading-[0.95] max-w-3xl">
                Scale, motion &amp; reliability — in every consignment.
              </h2>
              <p className="text-background/80 max-w-lg mt-6">
                A look inside the operations that keep {COMPANY.short}'s paper and paperboard moving worldwide.
              </p>
            </motion.div>
          </div>
        </div>

        <button
          onClick={toggle}
          data-testid="video-toggle"
          aria-label={playing ? "Pause video" : "Play video"}
          className="absolute top-6 right-6 z-10 w-12 h-12 flex items-center justify-center border border-background/40 text-background hover:bg-primary hover:border-primary transition-colors duration-300"
        >
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </button>
      </div>
    </section>
  );
};
